import { useEffect, useRef, useState } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { MARKDOWN, bytesToBase64, canKeepPriceListDocs, savePriceListDoc, textToBase64 } from "../../api/priceListDocs";
import Alert from "@mui/material/Alert";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import Chip from "@mui/material/Chip";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import LinearProgress from "@mui/material/LinearProgress";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useIdentity } from "../../auth/useIdentity";
import {
  ACCEPTED,
  applyProposal,
  DEFAULT_TERM_DISCOUNT,
  termDiscountProblems,
  type TermDiscount,
  bindingProblems,
  chargeKey,
  defaultBinding,
  sameItem,
  describeRetirable,
  type Binding,
  chargePrice,
  describeRow,
  headline,
  includesGst,
  planId,
  readProposal,
  forgetProposal,
  recentProposals,
  rememberProposal,
  type RecentProposal,
  soldWith,
  startProposal,
  uploadProblem,
  type ProposalJob,
} from "../../api/proposal";

const POLL_MS = 3000;

/**
 * A step that takes a while, shown as it runs: a moving bar and the seconds
 * so far (owner 2026-10-11: a single line read as "nothing is happening").
 */
export function Working({ label, since, hint }: { label: string; since: number; hint: string }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const secs = Math.max(0, Math.round((now - since) / 1000));
  return (
    <Box role="status" aria-live="polite" sx={{ mb: 2 }}>
      <Typography variant="body2" sx={{ mb: 0.5 }}>
        <strong>{label}</strong> · {secs} s · {hint}
      </Typography>
      <LinearProgress />
    </Box>
  );
}

function download(job: ProposalJob) {
  const blob = new Blob([job.rendered ?? ""], { type: "text/markdown" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `${job.namespace}-proposed-price-list.md`;
  a.click();
  URL.revokeObjectURL(a.href);
}

/**
 * Owner 2026-11-01: an error offers a mail to support. The screen's message and
 * the fingerprint only, never the document.
 */
export function supportMailto(message: string, fingerprint?: string): string {
  const body = `${message}${fingerprint ? `\n\nDocument fingerprint: ${fingerprint}` : ""}`;
  return `mailto:support@ankhos.io?subject=${encodeURIComponent("Plans from a document: error")}&body=${encodeURIComponent(body)}`;
}

function SupportLink({ message, fingerprint }: { message: string; fingerprint?: string | undefined }) {
  return (
    <Box sx={{ mt: 1 }}>
      <a href={supportMailto(message, fingerprint)}>Send this to support@ankhos.io</a>
    </Box>
  );
}

function List({ title, items }: { title: string; items: string[] }) {
  if (items.length === 0) return null;
  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="subtitle2">{title}</Typography>
      <ul style={{ marginTop: 4 }}>
        {items.map((x) => (
          <li key={x}>
            <Typography variant="body2">{x}</Typography>
          </li>
        ))}
      </ul>
    </Box>
  );
}

const FIELDS: { k: keyof Binding; label: string; width: number }[] = [
  { k: "key", label: "Key", width: 170 },
  { k: "category", label: "Category", width: 120 },
  { k: "sac", label: "SAC/HSN", width: 100 },
  { k: "uqc", label: "UQC", width: 70 },
  { k: "accounting", label: "Accounting", width: 170 },
];

/**
 * Approve and create (owner 2026-10-10, option C): TEST namespaces only.
 * The person sets each new charge's billing and RADIUS effect; radagent then
 * writes the components and price rules with this person's own sign-in.
 */
function ApplyPanel({ job, left, total, onStarted }: {
  job: ProposalJob;
  left: Set<string>;
  total: number;
  onStarted: () => void;
}) {
  const identity = useIdentity();
  const p = job.proposal ?? {};
  const oneTime = new Set((p.one_time ?? []).map((c) => c.charge));
  const all = job.needs_binding ?? [];
  const names = all.filter((n) => !left.has(n));
  const existing = job.existing ?? [];
  // charge -> the key of the item billing already holds that it is ("" = a new item)
  const [links, setLinks] = useState<Record<string, string>>(() =>
    Object.fromEntries(names.map((n) => [n, sameItem(n, existing)?.key ?? ""])),
  );
  const [bindings, setBindings] = useState<Record<string, Binding>>(() =>
    Object.fromEntries(
      names.map((n) => {
        const e = existing.find((x) => x.key === links[n]);
        return [n, { ...defaultBinding(n, e ? e.one_time : oneTime.has(n)), ...(e ? { key: e.key } : {}) }];
      }),
    ),
  );
  const link = (n: string, key: string) => {
    setLinks((l) => ({ ...l, [n]: key }));
    setBindings((b) => ({ ...b, [n]: { ...b[n]!, key: key || chargeKey(n) } }));
  };
  // A linked charge takes the name billing holds, so it is the same item.
  const rename = Object.fromEntries(
    names.flatMap((n) => {
      const e = existing.find((x) => x.key === links[n]);
      return e && e.charge !== n ? [[n, e.charge]] : [];
    }),
  );
  const set = (n: string, patch: Partial<Binding>) => setBindings((b) => ({ ...b, [n]: { ...b[n]!, ...patch } }));
  const problems = names.flatMap((n) => bindingProblems(bindings[n]!).map((x) => `${n} — ${x}`));
  const keys = names.map((n) => bindings[n]!.key);
  if (new Set(keys).size !== keys.length) problems.push("Two charges have the same key.");
  if (left.size >= total) problems.push("Every item is left out; tick at least one to approve.");
  const [discount, setDiscount] = useState<TermDiscount>(DEFAULT_TERM_DISCOUNT);
  // Switch off: nothing is priced from the 30-day price, so there is no discount to set (owner 2026-11-04).
  const onlyStated = job.proposal?.only_stated_terms === true;
  if (!onlyStated) problems.push(...termDiscountProblems(discount));
  const a = job.apply;
  const [retireOk, setRetireOk] = useState(false);
  const [restoreOk, setRestoreOk] = useState(false);
  const go = useMutation<ProposalJob, Error, { retire: boolean; restore: boolean }>({
    mutationFn: ({ retire, restore }) =>
      applyProposal(
        identity,
        job.namespace,
        job.id,
        Object.fromEntries(names.map((n) => [n, bindings[n]!])),
        [...left].sort(),
        retire,
        restore,
        rename,
        discount,
      ),
    onSuccess: onStarted,
  });
  // The compiler offers both lists only when together they settle every
  // finding, so the Approve below needs every list shown ticked.
  const toRetire = a?.status === "failed" ? (a.retirable ?? []) : [];
  const toRestore = a?.status === "failed" ? (a.restorable ?? []) : [];
  const decided = (!toRetire.length || retireOk) && (!toRestore.length || restoreOk);
  const decideLabel =
    toRetire.length && toRestore.length
      ? "Approve, retire and bring back"
      : toRetire.length
        ? "Approve and retire"
        : "Approve and bring back";
  const busy = go.isPending || a?.status === "running";
  const [applySince, setApplySince] = useState<number | null>(null);
  useEffect(() => {
    if (busy && applySince === null) setApplySince(Date.now());
    if (!busy && applySince !== null) setApplySince(null);
  }, [busy, applySince]);

  return (
    <Paper id="approve" sx={{ p: 2, mt: 2, scrollMarginTop: 72 }}>
      <Typography variant="h6">Approve and create — test namespace</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {job.namespace} is a test namespace, so this proposal can be applied here: its components and price rules are
        written to billing with your sign-in. Set how each new charge is billed, and what it raises on the subscriber's
        RADIUS login (for example <code>Simultaneous-Use +1 per unit</code>), then approve. Only new plans are created: a plan you already have is left as it is.
      </Typography>
      {names.length > 0 && (
        <Box sx={{ overflowX: "auto", mb: 1 }}>
          <Table size="small" aria-label="How each charge is billed">
            <TableHead>
              <TableRow>
                <TableCell>Charge</TableCell>
                {existing.length > 0 && <TableCell>Same item as in billing</TableCell>}
                <TableCell>Billing</TableCell>
                <TableCell>Charged per</TableCell>
                {FIELDS.map((f) => (
                  <TableCell key={f.k}>{f.label}</TableCell>
                ))}
                <TableCell>Internet service</TableCell>
                <TableCell>RADIUS effect</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {names.map((n) => {
                const b = bindings[n]!;
                return (
                  <TableRow key={n}>
                    <TableCell>{n}</TableCell>
                    {existing.length > 0 && (
                      <TableCell>
                        <TextField select size="small" value={links[n] ?? ""} sx={{ width: 230 }}
                          slotProps={{ select: { displayEmpty: true }, htmlInput: { "aria-label": `${n} same item as in billing` } }}
                          onChange={(e) => link(n, e.target.value)}>
                          <MenuItem value="">New item</MenuItem>
                          {existing.map((x) => (
                            <MenuItem key={x.key} value={x.key}>{x.charge}</MenuItem>
                          ))}
                        </TextField>
                      </TableCell>
                    )}
                    <TableCell>
                      <TextField select size="small" value={b.billing} sx={{ width: 130 }}
                        slotProps={{ htmlInput: { "aria-label": `${n} billing` } }}
                        onChange={(e) => set(n, { billing: e.target.value as Binding["billing"] })}>
                        <MenuItem value="RECURRING">RECURRING</MenuItem>
                        <MenuItem value="ONE_TIME">ONE_TIME</MenuItem>
                      </TextField>
                    </TableCell>
                    <TableCell>
                      {b.billing === "ONE_TIME" ? (
                        <TextField select size="small" value={b.charged_per ?? "sale"} sx={{ width: 190 }}
                          slotProps={{ htmlInput: { "aria-label": `${n} charged per` } }}
                          onChange={(e) => set(n, { charged_per: e.target.value === "address" ? "address" : undefined })}>
                          <MenuItem value="sale">Once per sale</MenuItem>
                          <MenuItem value="address">Once per installation address</MenuItem>
                        </TextField>
                      ) : (
                        <Typography variant="body2" color="text.secondary">by quantity</Typography>
                      )}
                    </TableCell>
                    {FIELDS.map((f) => (
                      <TableCell key={f.k}>
                        <TextField size="small" value={String(b[f.k] ?? "")} sx={{ width: f.width }}
                          slotProps={{ htmlInput: { "aria-label": `${n} ${f.label}` } }}
                          onChange={(e) => set(n, { [f.k]: e.target.value } as Partial<Binding>)} />
                      </TableCell>
                    ))}
                    <TableCell>
                      <Checkbox checked={b.internet_service} inputProps={{ "aria-label": `${n} internet service` }}
                        onChange={(e) => set(n, { internet_service: e.target.checked })} />
                    </TableCell>
                    <TableCell>
                      <TextField size="small" value={b.effect ?? ""} placeholder="—" sx={{ width: 230 }}
                        slotProps={{ htmlInput: { "aria-label": `${n} RADIUS effect` } }}
                        onChange={(e) => set(n, { effect: e.target.value.trim() ? e.target.value : null })} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      )}
      {!onlyStated && (<>
      <Typography variant="subtitle2" sx={{ mt: 1 }}>Term discount</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        A residential speed this proposal prices only for 30 days is also sold for 3, 6 and 12 months, at the 30-day
        price × months less this discount. A term the document prices keeps its own price. Every basic plan goes on sale
        when you approve; add-ons and one-time charges are sold with them.
      </Typography>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }} aria-label="Term discount">
        {(Object.keys(DEFAULT_TERM_DISCOUNT) as (keyof TermDiscount)[]).map((t) => (
          <TextField key={t} size="small" type="number" label={`${t === "365" ? "12 months" : `${Number(t) / 30} months`} (%)`}
            value={discount[t]} sx={{ width: 140 }} inputProps={{ min: 0, max: 50, step: 0.5 }}
            onChange={(e) => setDiscount((d) => ({ ...d, [t]: e.target.value === "" ? NaN : Number(e.target.value) }))} />
        ))}
      </Stack>
      </>)}
      {problems.length > 0 && <List title="Fix before approving" items={problems} />}
      {busy && applySince !== null && (
        <Working label="Creating components and prices" since={applySince}
          hint="this may take a few minutes; you may leave this page and come back" />
      )}
      <Typography variant="body2" sx={{ mb: 1 }}>
        Approving {total - left.size} of {total} items
        {left.size > 0 ? `; left out and never created: ${[...left].sort().join(", ")}` : ""}.
      </Typography>
      <Button variant="contained" color="warning" disabled={busy || problems.length > 0} onClick={() => go.mutate({ retire: false, restore: false })}>
        {busy ? "Creating components and prices…" : "Approve and create components and prices"}
      </Button>
      {go.isError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          <AlertTitle>Could not start</AlertTitle>
          {go.error.message}
          <SupportLink message={go.error.message} fingerprint={job.source.sha256.slice(0, 12)} />
        </Alert>
      )}
      {toRetire.length || toRestore.length ? (
        <Alert severity="warning" sx={{ mt: 2 }}>
          <AlertTitle>Nothing was written: this proposal differs from what billing holds</AlertTitle>
          {toRetire.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="subtitle2" sx={{ mt: 1 }}>
                {toRetire.length} item{toRetire.length === 1 ? "" : "s"} in billing from an earlier Approve
                {toRetire.length === 1 ? " is" : " are"} not in this proposal
              </Typography>
              Retire them to approve this proposal: they are no longer offered from tomorrow and their components are
              withdrawn. Nothing is deleted; an invoice already raised is not changed, and a subscriber already paying
              for one keeps renewing it at its last price. To stop selling a whole plan but keep its subscribers, use
              Plans › On sale instead.
              <List title="Would be retired" items={toRetire.map(describeRetirable)} />
              <Stack direction="row" spacing={1} alignItems="center">
                <Checkbox checked={retireOk} onChange={(e) => setRetireOk(e.target.checked)}
                  inputProps={{ "aria-label": "Retire these items" }} />
                <Typography variant="body2">Retire these {toRetire.length} items</Typography>
              </Stack>
            </Box>
          )}
          {toRestore.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="subtitle2" sx={{ mt: 1 }}>
                {toRestore.length} item{toRestore.length === 1 ? " was" : "s were"} retired earlier and{" "}
                {toRestore.length === 1 ? "is" : "are"} in this proposal again
              </Typography>
              Bring them back to approve this proposal: the same components are offered again straight away, and their
              prices — as this proposal states them — apply from tomorrow, never before the old prices stopped.
              <List title="Would come back" items={toRestore.map(describeRetirable)} />
              <Stack direction="row" spacing={1} alignItems="center">
                <Checkbox checked={restoreOk} onChange={(e) => setRestoreOk(e.target.checked)}
                  inputProps={{ "aria-label": "Bring these items back" }} />
                <Typography variant="body2">Bring these {toRestore.length} items back</Typography>
              </Stack>
            </Box>
          )}
          <Button variant="contained" color="warning" disabled={!decided || busy || problems.length > 0}
            onClick={() => go.mutate({ retire: toRetire.length > 0, restore: toRestore.length > 0 })}>
            {decideLabel}
          </Button>
        </Alert>
      ) : a?.status === "failed" ? (
        <Alert severity="error" sx={{ mt: 2 }}>
          <AlertTitle>Not created</AlertTitle>
          {a.error}
          <SupportLink message={a.error ?? "Not created"} fingerprint={job.source.sha256.slice(0, 12)} />
        </Alert>
      ) : null}
      {a?.status === "applied" && a.created && (
        <Alert severity="success" sx={{ mt: 2 }}>
          <AlertTitle>Created</AlertTitle>
          {a.created.component} components and {a.created.price_rule} price rules
          {a.effective_from ? `, in force from ${a.effective_from}` : ""}
          {a.unchanged ? `; ${a.unchanged} already there` : ""}
          {a.left_out?.length ? `; ${a.left_out.length} left out` : ""}
          {a.retired?.length ? `; ${a.retired.length} retired` : ""}
          {a.restored?.some((x) => x.includes(" component ")) ? `; ${a.restored.filter((x) => x.includes(" component ")).length} brought back` : ""}
          {a.left_alone?.length ? `; ${a.left_alone.length} you already have, left as they are` : ""}. See{" "}
          <RouterLink to="/plans/components">Components</RouterLink> and{" "}
          <RouterLink to="/plans/prices">Price list</RouterLink>.
          {a.plans_on_sale && (
            <Box sx={{ mt: 1 }}>
              {a.plans_on_sale.length
                ? `${a.plans_on_sale.length} basic plan${a.plans_on_sale.length === 1 ? "" : "s"} put on sale: ${a.plans_on_sale.join(", ")}.`
                : a.plans_not_on_sale?.length
                  ? ""
                  : "No new plan was put on sale."}
            </Box>
          )}
        </Alert>
      )}
      {a?.status === "applied" && (a.skipped_plans?.length ?? 0) > 0 && (
        <Alert severity="info" sx={{ mt: 1 }}>
          <AlertTitle>Plans in your file you already have</AlertTitle>
          These were not changed, and your file's price was not used. When you import customers, use the plan name shown.
          <List
            title="In your file → plan to use"
            items={a.skipped_plans!.map((x) => `${x.file_name} → ${x.plans.join(", ") || "not on sale yet"}`)}
          />
        </Alert>
      )}
      {a?.status === "applied" && (a.plans_not_on_sale?.length ?? 0) > 0 && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          <AlertTitle>Prices created, but {a.plans_not_on_sale!.length} plan(s) not put on sale</AlertTitle>
          <List title="Not on sale" items={a.plans_not_on_sale!} />
          Approve again to retry, or use Plans › Create plan › Put all basic plans on sale.
        </Alert>
      )}
    </Paper>
  );
}

export function Result({ job, onApplyStarted, onDiscard }: {
  job: ProposalJob;
  onApplyStarted: () => void;
  onDiscard: () => void;
}) {
  const h = headline(job);
  const p = job.proposal ?? {};
  // Items the person unticked: never created (owner 2026-10-10).
  const [left, setLeft] = useState<Set<string>>(() => new Set());
  const choosing = Boolean(job.can_apply && job.can_leave_out);
  const total = (p.plans?.length ?? 0) + (p.addons?.length ?? 0) + (p.one_time?.length ?? 0);
  const include = (id: string, label: string) =>
    choosing && (
      <TableCell padding="checkbox">
        <Checkbox
          checked={!left.has(id)}
          inputProps={{ "aria-label": `Include ${label}` }}
          onChange={(e) =>
            setLeft((s) => {
              const n = new Set(s);
              if (e.target.checked) n.delete(id);
              else n.add(id);
              return n;
            })
          }
        />
      </TableCell>
    );
  return (
    <Box sx={{ mt: 2 }}>
      <Alert severity={h.severity} sx={{ mb: 2 }}>
        <AlertTitle>{h.title}</AlertTitle>
        {job.source.filename} · document fingerprint {job.source.sha256.slice(0, 12)}
        {job.status !== "running" && job.apply?.status !== "running" && (
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            {job.status === "proposed" && job.can_apply && (
              <Button size="small" variant="contained" color="warning"
                onClick={() => document.getElementById("approve")?.scrollIntoView({ behavior: "smooth" })}>
                Go to Approve
              </Button>
            )}
            {/* Nothing was created from it; radagent forgets it within the hour. */}
            <Button size="small" variant="outlined" color="inherit" onClick={onDiscard}>
              Discard this proposal
            </Button>
          </Stack>
        )}
      </Alert>
      {job.status === "rejected" && (
        <List title="Each figure must appear in the document, word for word" items={job.quote_failures ?? []} />
      )}
      {(job.warnings?.length ?? 0) > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}><List title="You already sell these" items={job.warnings!} /></Alert>
      )}
      {(job.working?.length ?? 0) > 0 && (
        <List title="How each price was calculated"
          items={job.working!.map((w) => `${w.speed_mbps} Mbps, ${w.term_days} days: ${w.working}`)} />
      )}
      {job.status === "needs_answers" && <List title="Answer these, then upload again" items={(job.problems ?? []).filter((x) => x !== "price_includes_tax")} />}
      {job.status === "failed" && (
        <>
          <Typography variant="body2">{job.error}</Typography>
          <SupportLink message={job.error ?? "Failed"} fingerprint={job.source.sha256.slice(0, 12)} />
        </>
      )}
      {job.status === "proposed" && (
        <>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {job.rows} price-list rows would be created{p.effective_from ? `, from ${p.effective_from}` : ""}. The
            owner approves a proposal; until then nothing here is charged.
          </Typography>
          {job.changes && (
            <>
              <List title="New compared with your approved price list" items={job.changes.added.map(describeRow)} />
              <List title="Price changed" items={job.changes.repriced.map(describeRow)} />
              <List title="No longer in the document" items={job.changes.dropped.map(describeRow)} />
            </>
          )}
          <List
            title="Read but not copied — add these yourself if they should be sold"
            items={(p.excluded ?? []).map((x) => `${x.charge}: ${x.reason} — “${x.quote}”`)}
          />
          <List
            title="Charges whose tax code, billing and effect on the login a person must still set"
            items={job.needs_binding ?? []}
          />
          {(p.plans?.length ?? 0) > 0 && (
            <Paper sx={{ mb: 2 }}>
              <Table size="small" aria-label="Proposed plans">
                <TableHead>
                  <TableRow>
                    {choosing && <TableCell padding="checkbox">Include</TableCell>}
                    <TableCell>Plan</TableCell>
                    <TableCell align="right">Price (final incl. GST)</TableCell>
                    <TableCell>Read from</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {p.plans!.map((x) => (
                    <TableRow key={`${x.product}-${x.speed_mbps}-${x.term_days}`}>
                      {include(planId(x), `${x.product} ${x.speed_mbps} Mbps, ${x.term_days} days`)}
                      <TableCell>
                        {x.product} {x.speed_mbps} Mbps, {x.term_days} day{x.term_days === 1 ? "" : "s"}, {x.devices || 1} login{(x.devices || 1) === 1 ? "" : "s"}
                      </TableCell>
                      <TableCell align="right">{chargePrice(x.price, includesGst(x, p.prices_include_gst))}</TableCell>
                      <TableCell>“{x.quote}”</TableCell>
                    </TableRow>
                  ))}
                  {[...(p.addons ?? []), ...(p.one_time ?? [])].map((c) => (
                    <TableRow key={c.charge}>
                      {include(c.charge, c.charge)}
                      <TableCell>
                        {c.charge}
                        <Typography variant="caption" display="block" color="text.secondary">
                          {soldWith(c)}
                          {c.priced_by ? ` · ${c.priced_by}` : ""}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {c.priced_by === "formula" || c.priced_by === "one price"
                          ? Object.values(c.prices)
                              .map((v) => chargePrice(v, includesGst(c, p.prices_include_gst)))
                              .join(", ")
                          : Object.entries(c.prices)
                              .map(([t, v]) => `${chargePrice(v, includesGst(c, p.prices_include_gst))} (${t} d)`)
                              .join(", ")}
                      </TableCell>
                      <TableCell>“{c.quote}”</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}
          <Button variant="outlined" onClick={() => download(job)}>
            Download the proposed price list
          </Button>
          {job.can_apply ? (
            <>
              {!job.can_leave_out && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  This radagent cannot leave items out yet: Approve creates every plan and charge above.
                </Alert>
              )}
              <ApplyPanel job={job} left={left} total={total} onStarted={onApplyStarted} />
            </>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Creating plans from this screen is not open for your account yet. You can download this list and create
              the plans under Plans › Create plan, or write to support@ankhos.io.
            </Typography>
          )}
        </>
      )}
    </Box>
  );
}

/**
 * Plans › Propose from a document (plan builder design §9.13 step 3, owner
 * 2026-10-10). Upload a rate card; the AI copies its prices, every figure is
 * checked against the document, and the result is a price-list PROPOSAL the
 * owner approves. This screen writes no price.
 */
export function ProposePriceList() {
  const identity = useIdentity();
  const own = identity.namespace ?? "";
  // The job id and namespace live in the address, so leaving the page or
  // reloading it comes back to the same proposal (radagent keeps it an hour).
  const [params, setParams] = useSearchParams();
  const [ns, setNs] = useState(params.get("ns") || own);
  const [file, setFile] = useState<File | null>(null);
  const [effectiveFrom, setEffectiveFrom] = useState("");
  const jobId = params.get("job");
  const setJobId = (id: string | null) =>
    setParams((p) => {
      const q = new URLSearchParams(p);
      if (id) {
        q.set("job", id);
        q.set("ns", ns);
      } else {
        q.delete("job");
      }
      return q;
    }, { replace: true });
  const problem = file ? uploadProblem(file) : null;

  const [openedAt, setOpenedAt] = useState(() => Date.now());
  const [uploadSince, setUploadSince] = useState(() => Date.now());
  // Owner 2026-10-29: the rate card and the price list read from it are KEPT
  // (Plans › Price-list documents), so the original is never lost. Best effort:
  // a failure to keep is said, and never stops the reading.
  const keeps = canKeepPriceListDocs(identity.roles);
  const [kept, setKept] = useState<string | null>(null);
  const keptOriginal = useRef(new Map<string, number>());
  const keepOriginal = async (j: ProposalJob, f: File) => {
    try {
      const d = await savePriceListDoc(identity, ns, {
        kind: "original", title: f.name.replace(/\.[^.]+$/, ""), filename: f.name,
        mediaType: f.type || "application/octet-stream", contentBase64: bytesToBase64(new Uint8Array(await f.arrayBuffer())),
      });
      keptOriginal.current.set(j.id, d.id);
      setKept(`Kept the rate card as document #${d.id}.`);
    } catch (e) {
      setKept(`The rate card was not kept: ${(e as Error).message}`);
    }
  };
  // Owner 2026-11-02: on by default (the 26/10 rule); off sells only the terms the file states.
  const [longerTerms, setLongerTerms] = useState(true);
  const start = useMutation<ProposalJob, Error>({
    mutationFn: () => startProposal(identity, ns, file!, effectiveFrom || undefined, !longerTerms),
    onMutate: () => setUploadSince(Date.now()),
    onSuccess: (j) => {
      setOpenedAt(Date.now());
      setJobId(j.id);
      if (keeps && file) void keepOriginal(j, file);
    },
  });

  const job = useQuery({
    queryKey: ["proposal", ns, jobId],
    enabled: Boolean(identity.token && jobId),
    retry: false,
    queryFn: ({ signal }) => readProposal(identity, ns, jobId!, signal),
    refetchInterval: (q) =>
      q.state.data?.status === "running" || q.state.data?.apply?.status === "running" || !q.state.data ? POLL_MS : false,
    // A rate card takes a minute or two; reading it in another tab must not stop the poll.
    refetchIntervalInBackground: true,
  });

  const [recent, setRecent] = useState<RecentProposal[]>(() => recentProposals());
  const seen = job.data;
  // Keep each proposal once, the moment it is read (parent: its rate card).
  useEffect(() => {
    if (!keeps || !seen?.rendered || seen.status === "running") return;
    const key = `kept-proposal-${seen.id}`;
    try {
      if (localStorage.getItem(key)) return;
      localStorage.setItem(key, "1");
    } catch {
      /* no storage: may keep twice, never zero times */
    }
    const parentId = keptOriginal.current.get(seen.id);
    savePriceListDoc(identity, seen.namespace, {
      kind: "proposal", title: `Read from ${seen.source.filename}`,
      filename: `${seen.namespace}-proposed-price-list.md`, mediaType: MARKDOWN, contentBase64: textToBase64(seen.rendered),
      ...(parentId !== undefined ? { parentId } : {}),
    }).then(
      (d) => setKept((k) => `${k ? k + " " : ""}Kept the proposal as document #${d.id}.`),
      (e: Error) => setKept((k) => `${k ? k + " " : ""}The proposal was not kept: ${e.message}`),
    );
  }, [keeps, seen?.id, seen?.status, seen?.rendered, seen?.namespace, seen?.source.filename, identity]);
  useEffect(() => {
    if (seen?.id) setRecent(rememberProposal({ id: seen.id, namespace: seen.namespace, filename: seen.source.filename }));
  }, [seen?.id, seen?.namespace, seen?.source.filename]);
  const open = (r: RecentProposal) => {
    setOpenedAt(Date.now());
    setNs(r.namespace);
    setParams({ ns: r.namespace, job: r.id }, { replace: true });
  };

  const reset = () => {
    setJobId(null);
    start.reset();
  };

  return (
    <Box sx={{ p: 2, maxWidth: 1000 }}>
      <Typography variant="h5" gutterBottom>
        Propose a price list from a document <Chip size="small" color="secondary" icon={<AutoAwesomeIcon />} label="AI" />
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Upload your rate card (PDF, Word or text, up to 5 MB). The AI copies its prices and quotes the line each came
        from; every quote is checked against your document. The result is a proposal for the owner to approve — nothing
        is charged from this screen.
      </Typography>
      {kept && (
        <Alert severity={kept.includes("not kept") ? "warning" : "info"} sx={{ mb: 2 }}>
          {kept} Find it under Plans › Price-list documents.
        </Alert>
      )}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 2 }}>
        <Button size="small" variant="text" href="/samples/sample-rate-card.pdf" download>
          Download a sample rate card (PDF)
        </Button>
        <Button size="small" variant="text" href="/samples/sample-plans.csv" download>
          Download a sample plans file (CSV)
        </Button>
        <Button size="small" variant="text" href="/help/rate-card-guide.html" target="_blank" rel="noopener">
          How to prepare your rate card
        </Button>
      </Stack>
      <FormControlLabel sx={{ display: "block", mb: 1 }}
        control={<Switch checked={longerTerms} onChange={(e) => { setLongerTerms(e.target.checked); reset(); }} />}
        label={longerTerms ? "Also sell 90, 180 and 365 days at the term discount" : "Only the terms the file states; a 30-day price is always needed"} />
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 2 }}>
        <TextField size="small" label="Namespace" value={ns} sx={{ minWidth: 300 }}
          onChange={(e) => { setNs(e.target.value.trim().toLowerCase()); reset(); }} />
        <TextField size="small" type="date" label="Prices take effect" value={effectiveFrom}
          slotProps={{ inputLabel: { shrink: true } }} helperText="Optional; a change needs a future date"
          onChange={(e) => setEffectiveFrom(e.target.value)} />
        <Button variant="outlined" component="label" sx={{ height: 40 }}>
          {file ? file.name : "Choose file"}
          <input hidden type="file" accept={ACCEPTED} aria-label="Rate card"
            onChange={(e) => { setFile(e.target.files?.[0] ?? null); reset(); }} />
        </Button>
        <Button variant="contained" sx={{ height: 40 }}
          disabled={!file || Boolean(problem) || !ns || start.isPending || job.data?.status === "running"}
          onClick={() => start.mutate()}>
          {start.isPending ? "Uploading…" : "Propose"}
        </Button>
      </Stack>
      {problem && <Alert severity="warning" sx={{ mb: 2 }}>{problem}</Alert>}
      {start.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <AlertTitle>Could not start the proposal</AlertTitle>
          {start.error.message}
        </Alert>
      )}
      {job.isError && /\bHTTP 404\b/.test((job.error as Error).message) ? (
        <Alert severity="warning" sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small"
              onClick={() => { if (jobId) setRecent(forgetProposal(jobId)); reset(); }}>
              Start again
            </Button>
          }>
          <AlertTitle>This proposal is no longer held</AlertTitle>
          radagent keeps a proposal for an hour and forgets every proposal when it restarts (for example after an
          update). Nothing was created from it. Choose the file and press Propose again.
        </Alert>
      ) : job.isError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          <AlertTitle>Could not read the proposal back</AlertTitle>
          {(job.error as Error).message}
        </Alert>
      ) : null}
      {start.isPending && (
        <Working label="Uploading the document" since={uploadSince} hint="a few seconds" />
      )}
      {jobId && !job.isError && (!job.data || job.data.status === "running") && (
        <Working label="Reading the document" since={recent.find((r) => r.id === jobId)?.at ?? openedAt}
          hint="this may take a few minutes; you may leave this page and come back from Recent proposals" />
      )}
      {recent.length > 0 && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1">Recent proposals</Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Uploaded or opened in this browser in the last hour; radagent keeps a proposal an hour.
          </Typography>
          {recent.map((r) => {
            const here = r.id === jobId;
            const reading = here && (!job.data || job.data.status === "running") && !job.isError;
            return (
              <Box key={r.id} sx={{ display: "flex", gap: 1, alignItems: "center", mb: 0.5 }}>
                <Button size="small" variant={here ? "contained" : "outlined"} disabled={here} onClick={() => open(r)}>
                  {reading ? "Processing… wait" : here ? "Shown below" : "Open"}
                </Button>
                <Typography variant="body2">
                  {r.filename} · {r.namespace} · {new Date(r.at).toLocaleTimeString()}
                  {reading ? " · reading…" : ""}
                </Typography>
              </Box>
            );
          })}
        </Paper>
      )}
      {job.data && (
        <Result job={job.data} onApplyStarted={() => void job.refetch()}
          onDiscard={() => { if (jobId) setRecent(forgetProposal(jobId)); reset(); }} />
      )}
    </Box>
  );
}
